import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useProject } from "@/context/ProjectContext";

const OPERATORS = [">", "<", ">=", "<=", "=="];

export function RuleForm({ onCreated }) {
  const [sites, setSites] = useState([]);
  const [points, setPoints] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [pointId, setPointId] = useState("");
  const [operator, setOperator] = useState(">");
  const [threshold, setThreshold] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/sites").then(({ data }) => setSites(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setPoints([]);
    setPointId("");
    if (!siteId) return;
    api
      .get(`/sites/${encodeURIComponent(siteId)}/points`)
      .then(({ data }) => setPoints((data || []).filter((p) => p.kind === "Number")))
      .catch(() => {});
  }, [siteId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/alerts/rules", {
        point_id: pointId,
        operator,
        threshold: Number(threshold),
        label: label.trim() || null,
      });
      toast.success("Alert rule created");
      setThreshold("");
      setLabel("");
      onCreated();
    } catch (e) {
      toast.error("Failed to create rule", { description: formatApiErrorDetail(e?.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="k-card p-6" data-testid="rule-form">
      <div className="k-label flex items-center gap-2">
        <Plus className="h-3.5 w-3.5" /> New threshold rule
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className="k-sublabel">Site</label>
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required className="k-input mt-2" data-testid="rule-site-select">
            <option value="">Select site…</option>
            {sites.map((s) => (
              <option key={s.site_id} value={s.site_id}>{s.dis}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="k-sublabel">Point</label>
          <select value={pointId} onChange={(e) => setPointId(e.target.value)} required disabled={!siteId} className="k-input mt-2" data-testid="rule-point-select">
            <option value="">{siteId ? "Select point…" : "Pick a site first"}</option>
            {points.map((p) => (
              <option key={p.point_id} value={p.point_id}>{`${p.dis}${p.unit ? ` (${p.unit})` : ""}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="k-sublabel">Condition</label>
          <select value={operator} onChange={(e) => setOperator(e.target.value)} className="k-input mt-2" data-testid="rule-operator-select">
            {OPERATORS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="k-sublabel">Threshold</label>
          <input
            type="number"
            step="any"
            required
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className="k-input mt-2"
            placeholder="e.g. 75"
            data-testid="rule-threshold-input"
          />
        </div>
        <div>
          <label className="k-sublabel">Label (optional)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="k-input mt-2"
            placeholder="High zone temp"
            data-testid="rule-label-input"
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button type="submit" disabled={busy || !pointId || threshold === ""} data-testid="rule-create-btn" className="k-btn !py-2">
          <Plus className="h-3.5 w-3.5" /> {busy ? "Creating…" : "Create rule"}
        </button>
      </div>
    </form>
  );
}
